import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, GitCompareArrows, Plus } from "lucide-react";
import { useMunicipality } from "@/context/MunicipalityContext";
import {
  COMPARISON_HISTORY_QUERY_KEY,
  comparisonQueryKey,
  useCodeLibraryCompare,
  type ComparisonDraft,
} from "@/context/CodeLibraryCompareContext";
import {
  createCodeLibraryComparison,
  getCodeLibraryIncludedMunicipalities,
  type CodeLibraryMunicipality,
} from "@/api/code_library_compare";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { ModuleWorkspaceBody } from "@/components/module_summary/ModuleWorkspaceBody";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";
import { ComparisonSession } from "@/components/code_library/ComparisonSession";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";

const STARTER_QUESTIONS = [
  "Compare short-term rental permit requirements and renewal rules.",
  "Compare fence height limits for residential properties.",
  "Compare accessory dwelling unit requirements, including lot size and parking."
];

function NewComparisonForm({
  libraryMunicipality,
  draft,
}: {
  libraryMunicipality: CodeLibraryMunicipality;
  draft: ComparisonDraft | null;
}) {
  const queryClient = useQueryClient();
  const { openComparison } = useCodeLibraryCompare();
  const libraryMunicipalityId = libraryMunicipality.id;
  const [primaryMunicipalityId, setPrimaryMunicipalityId] = useState<number>(
    draft?.homeMunicipalityId ?? libraryMunicipalityId,
  );
  const [comparisonMunicipalityId, setComparisonMunicipalityId] = useState<number>(
    draft?.comparisonMunicipalityId ?? 0,
  );
  const [prompt, setPrompt] = useState(draft?.prompt ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: includedMunicipalities = [],
    isLoading: comparisonOptionsLoading,
  } = useQuery({
    queryKey: ["code_library_included_municipalities", libraryMunicipalityId],
    queryFn: () => getCodeLibraryIncludedMunicipalities(libraryMunicipalityId),
  });

  // Primary/comparison may be the library owner or any included peer.
  const communityOptions = useMemo(() => {
    const peersWithoutOwner = includedMunicipalities.filter((m) => m.id !== libraryMunicipalityId);
    return [
      { id: libraryMunicipality.id, name: libraryMunicipality.name, state: libraryMunicipality.state },
      ...peersWithoutOwner,
    ];
  }, [libraryMunicipality, libraryMunicipalityId, includedMunicipalities]);

  const comparisonOptions = useMemo(
    () => communityOptions.filter((m) => m.id !== primaryMunicipalityId),
    [communityOptions, primaryMunicipalityId],
  );

  // A library switch starts the primary community over at the new library's owner. The first
  // render keeps a draft's primary community.
  const appliedLibraryId = useRef(libraryMunicipalityId);
  useEffect(() => {
    if (appliedLibraryId.current === libraryMunicipalityId) return;
    appliedLibraryId.current = libraryMunicipalityId;
    setPrimaryMunicipalityId(libraryMunicipalityId);
  }, [libraryMunicipalityId]);

  // A retry draft can name a community this library does not include. Correct that during
  // render, before the select mounts. An unmatched value makes Radix's hidden select fire a
  // change to an empty value, which overwrites an effect and leaves the placeholder showing.
  if (
    !comparisonOptionsLoading &&
    !communityOptions.some((municipality) => municipality.id === primaryMunicipalityId)
  ) {
    setPrimaryMunicipalityId(libraryMunicipalityId);
  }
  if (!comparisonOptionsLoading && !comparisonOptions.some((municipality) => municipality.id === comparisonMunicipalityId)) {
    const fallbackComparisonId = comparisonOptions[0]?.id || 0;
    if (fallbackComparisonId !== comparisonMunicipalityId) {
      setComparisonMunicipalityId(fallbackComparisonId);
    }
  }

  const noComparisonOptions = !comparisonOptionsLoading && comparisonOptions.length === 0;
  const canCompare = Boolean(primaryMunicipalityId && comparisonMunicipalityId && comparisonOptions.length);

  const handleCompare = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!primaryMunicipalityId || !comparisonMunicipalityId) {
      setError("Select both communities to compare.");
      return;
    }
    if (!prompt.trim()) {
      setError("Enter a comparison question.");
      return;
    }

    setSubmitting(true);
    try {
      const job = await createCodeLibraryComparison({
        libraryMunicipalityId,
        homeMunicipalityId: primaryMunicipalityId,
        comparisonMunicipalityId,
        prompt: prompt.trim(),
      });
      queryClient.setQueryData(comparisonQueryKey(job.id), job);
      void queryClient.invalidateQueries({ queryKey: COMPARISON_HISTORY_QUERY_KEY });
      openComparison(job.id);
    } catch (err: unknown) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "The comparison could not be started. Your question is still here; try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleCompare} className="space-y-6 rounded-lg border bg-card p-4 sm:p-6">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">New comparison</h2>
        <p className="text-sm text-muted-foreground">
          Ask one question about two communities. You get each community&apos;s answer with its sources and a
          summary of how they differ.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-semibold" htmlFor="home-municipality">
            Primary community
          </label>
          <Select
            value={primaryMunicipalityId ? String(primaryMunicipalityId) : ""}
            onValueChange={(value) => setPrimaryMunicipalityId(Number(value) || 0)}
            disabled={comparisonOptionsLoading || submitting}
          >
            <SelectTrigger id="home-municipality">
              <SelectValue placeholder={comparisonOptionsLoading ? "Loading communities..." : "Select a primary community"} />
            </SelectTrigger>
            <SelectContent>
              {communityOptions.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {formatMunicipalityLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold" htmlFor="comparison-municipality">
            Comparison community
          </label>
          {noComparisonOptions ? (
            <p className="text-sm text-muted-foreground">
              No comparison communities are set up for {formatMunicipalityLabel(libraryMunicipality)}. Contact
              Customer Success to add one.
            </p>
          ) : (
            <Select
              value={comparisonMunicipalityId ? String(comparisonMunicipalityId) : ""}
              onValueChange={(value) => setComparisonMunicipalityId(Number(value) || 0)}
              disabled={comparisonOptionsLoading || !comparisonOptions.length || submitting}
            >
              <SelectTrigger id="comparison-municipality">
                <SelectValue
                  placeholder={comparisonOptionsLoading ? "Loading communities..." : "Select a comparison community"}
                />
              </SelectTrigger>
              <SelectContent>
                {comparisonOptions.map((m) => (
                  <SelectItem key={m.id} value={String(m.id)}>
                    {formatMunicipalityLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-semibold" htmlFor="compare-prompt">
          Comparison question
        </label>
        <Textarea
          id="compare-prompt"
          rows={4}
          placeholder="Example: Compare short-term rental permit requirements, renewal timelines, and penalties."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={submitting}
        />
        <p className="text-xs text-muted-foreground">
          Be specific. Name the topic and what matters, such as permit steps, thresholds, timelines, fees, or
          enforcement.
        </p>
        <div className="flex flex-wrap gap-2">
          {STARTER_QUESTIONS.map((question) => (
            <Button
              key={question}
              type="button"
              variant="outline"
              className="h-auto whitespace-normal py-1.5 text-left text-xs"
              disabled={submitting}
              onClick={() => setPrompt(question)}
            >
              {question}
            </Button>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <Button type="submit" disabled={submitting || !canCompare}>
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Starting...
          </>
        ) : (
          <>
            <GitCompareArrows className="mr-2 h-4 w-4" />
            Compare
          </>
        )}
      </Button>
    </form>
  );
}

export function CodeLibraryComparePortal() {
  const {
    municipalities: libraryMunicipalities,
    municipality: activeMunicipality,
    isLoading: libraryMunicipalitiesLoading,
    setMunicipalityId,
  } = useMunicipality();
  const { activeComparisonId, startNewComparison, draft } = useCodeLibraryCompare();

  // The shared municipality context is the single source of truth for the library
  // scope; it already restores the stored selection and auto-selects the only home.
  const libraryMunicipality = useMemo(
    () => libraryMunicipalities.find((m) => m.id === activeMunicipality?.id),
    [libraryMunicipalities, activeMunicipality?.id],
  );

  const noLibraryMunicipalities = !libraryMunicipalitiesLoading && libraryMunicipalities.length === 0;

  const actions = (
    <>
      {libraryMunicipalities.length > 1 && activeComparisonId == null && (
        <div className="w-64">
          <Select
            value={libraryMunicipality ? String(libraryMunicipality.id) : ""}
            onValueChange={(value) => {
              const id = Number(value) || 0;
              if (id) setMunicipalityId(id);
            }}
            disabled={libraryMunicipalitiesLoading}
          >
            <SelectTrigger aria-label="Select a municipality">
              <SelectValue placeholder="Select a municipality" />
            </SelectTrigger>
            <SelectContent>
              {libraryMunicipalities.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {formatMunicipalityLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {activeComparisonId != null && (
        <Button variant="outline" size="sm" onClick={() => startNewComparison()}>
          <Plus className="h-4 w-4" />
          <span className="ml-2">New comparison</span>
        </Button>
      )}
    </>
  );

  return (
    <div className={moduleWorkspaceShellClass}>
      <ModuleWorkspaceHeader
        title="Code Library Compare"
        subtitle="Compare local laws across two communities."
        actions={actions}
        density="compact"
      />
      <ModuleWorkspaceBody width="narrow" contentClassName="max-w-5xl">
        {activeComparisonId != null ? (
          <ComparisonSession key={activeComparisonId} comparisonId={activeComparisonId} />
        ) : libraryMunicipalitiesLoading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading municipalities">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : noLibraryMunicipalities ? (
          <p className="text-sm text-muted-foreground">No municipalities are assigned to your account.</p>
        ) : !libraryMunicipality ? (
          <p className="text-sm text-muted-foreground">Choose a municipality above to load comparison communities.</p>
        ) : (
          <NewComparisonForm
            key={JSON.stringify(draft)}
            libraryMunicipality={libraryMunicipality}
            draft={draft}
          />
        )}
      </ModuleWorkspaceBody>
    </div>
  );
}
